import { Navigate, useParams } from 'react-router-dom';

import { LoadingState } from '../../components/loading-state';
import { useGetProviderInfoQuery } from '../../store/hooks';
import { resourceListPath, resourcesRootPath } from '../../utils/resource-routes';

/**
 * Forwards `provider/:providerId/<resource>` to the canonical user-scoped
 * resource list (GDN-2723), or to the resource catalog when no type is given.
 *
 * The old URL named the user but not the organization; the canonical one needs
 * both, so the org is resolved from the user record. A user with no
 * organization has no resources to scope — resources are always org-scoped —
 * so that case goes back to their detail page.
 */
export function ProviderResourceListRedirect({
  resourceType,
}: {
  resourceType?: string;
}) {
  const { providerId: raw } = useParams<{ providerId?: string }>();
  const providerId = raw ? decodeURIComponent(raw) : '';

  const { data: provider, isLoading } = useGetProviderInfoQuery(
    { providerId },
    { skip: !providerId },
  );

  if (!providerId) return <Navigate to="/admin-v2" replace />;
  if (isLoading) return <LoadingState message="Opening resources..." />;

  const organizationId = provider?.organization_id;
  if (!organizationId) {
    return (
      <Navigate to={`/admin-v2/provider/${encodeURIComponent(providerId)}`} replace />
    );
  }

  const scope = { organizationId, userId: providerId };
  return (
    <Navigate
      to={
        resourceType
          ? resourceListPath(scope, resourceType)
          : resourcesRootPath(scope)
      }
      replace
    />
  );
}
